import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { stagePatchPayload } from '@/adapters/deals'
import type { Deal, DealStage, Proposta } from '@/types'
import { normalizeProposalsList } from '@/utils/proposalApi'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { resolveProposalPublicLink } from './proposalPublicLink'
import {
  needsRenegotiationDraft,
} from './proposalRenegotiation'

export function useProposalTabProposals(deal: Deal, onDealPatched: (updates: Partial<Deal>) => void) {
  const [proposals, setProposals] = useState<Proposta[]>([])
  const [loadingProposals, setLoadingProposals] = useState(true)
  const [preparingRenegotiationDraft, setPreparingRenegotiationDraft] = useState(false)

  const fetchProposals = useCallback(async () => {
    const { data } = await api.get(`/deals/${deal.id}/proposals`)
    return normalizeProposalsList(data)
  }, [deal.id])

  const ensureRenegotiationDraft = useCallback(async () => {
    setPreparingRenegotiationDraft(true)
    try {
      await api.post(`/deals/${deal.id}/proposals/renegotiation-draft`)
      const list = await fetchProposals()
      setProposals(list)
      return list
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao preparar rascunho da proposta'))
      return null
    } finally {
      setPreparingRenegotiationDraft(false)
    }
  }, [deal.id, fetchProposals])

  const loadProposals = useCallback(async () => {
    setLoadingProposals(true)
    try {
      let list = await fetchProposals()
      if (needsRenegotiationDraft(list, deal.etapa)) {
        const refreshed = await ensureRenegotiationDraft()
        if (refreshed) list = refreshed
      }
      setProposals(list)
    } catch {
      setProposals([])
    } finally {
      setLoadingProposals(false)
    }
  }, [deal.etapa, fetchProposals, ensureRenegotiationDraft])

  useEffect(() => {
    void loadProposals()
  }, [loadProposals, deal.etapa])

  const handleDeleteProposal = useCallback(
    async (id: string, onAfterDelete?: () => void) => {
      try {
        await api.delete(`/propostas/${id}`)
        await loadProposals()
        onAfterDelete?.()
        toast.success('Proposta removida')
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao remover proposta'))
      }
    },
    [loadProposals],
  )

  const handleCopyLink = useCallback((propostaOrId: Proposta | string) => {
    const link =
      typeof propostaOrId === 'string'
        ? `${window.location.origin}/p/${propostaOrId}`
        : resolveProposalPublicLink(propostaOrId)
    navigator.clipboard.writeText(link)
    toast.success('Link copiado!')
  }, [])

  const handleGerarLink = useCallback(
    async (params: {
      itensCount: number
      editingId: string | null
      buildPayload: () => Record<string, unknown>
      onSuccess: () => void
      setGeneratingLink: (v: boolean) => void
    }) => {
      if (params.itensCount === 0) {
        toast.error('Adicione pelo menos um item à proposta para gerar o link')
        return
      }
      params.setGeneratingLink(true)
      try {
        let proposalId = params.editingId
        if (!proposalId) {
          const { data } = await api.post('/propostas', { ...params.buildPayload(), status: 'rascunho' })
          proposalId = (data.data ?? data).id
        }
        const { data } = await api.post(`/propostas/${proposalId}/gerar-link`)
        const saved = (data.data?.proposta ?? data.proposta ?? data.data ?? data) as Proposta | undefined
        const link = saved ? resolveProposalPublicLink(saved) : resolveProposalPublicLink({ id: proposalId } as Proposta)
        navigator.clipboard.writeText(link)
        toast.success('Link gerado e copiado!')
        await loadProposals()
        if (deal.etapa === 'proposta_elaboracao') {
          await api.patch(`/deals/${deal.id}/stage`, stagePatchPayload('proposta_enviada' as DealStage))
          onDealPatched({ etapa: 'proposta_enviada' as DealStage })
        }
        params.onSuccess()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao gerar link'))
      } finally {
        params.setGeneratingLink(false)
      }
    },
    [deal.etapa, deal.id, onDealPatched, loadProposals],
  )

  return {
    proposals,
    setProposals,
    loadingProposals,
    preparingRenegotiationDraft,
    loadProposals,
    ensureRenegotiationDraft,
    handleDeleteProposal,
    handleCopyLink,
    handleGerarLink,
  }
}
